import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Search, Download, Calendar, User, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  register: 'Registro',
  create: 'Crear',
  update: 'Actualizar',
  delete: 'Eliminar',
  create_user: 'Crear usuario',
  update_user: 'Actualizar usuario',
  delete_user: 'Eliminar usuario'
};

const RESOURCE_LABELS = {
  auth: 'Autenticación',
  user: 'Usuario',
  patient: 'Paciente',
  therapist: 'Terapeuta',
  report: 'Reporte',
  resource: 'Recurso',
  appointment: 'Cita',
  institution: 'Institución'
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    action: '',
    limit: 100
  });
  const [users, setUsers] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const queryData = {};
      if (filters.start_date) queryData.start_date = filters.start_date;
      if (filters.end_date) queryData.end_date = filters.end_date;
      if (filters.user_id) queryData.user_id = filters.user_id;
      if (filters.action) queryData.action = filters.action;
      if (filters.limit) queryData.limit = filters.limit;

      const { data } = await axios.post(
        `${API}/admin/audit-logs/query`,
        queryData,
        { withCredentials: true }
      );
      setLogs(data.logs);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al cargar logs de auditoría');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, [fetchUsers, fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Usuario', 'Acción', 'Recurso', 'ID Recurso', 'Estado'];
    const rows = logs.map(log => [
      new Date(log.timestamp).toLocaleString(),
      log.user_id,
      ACTION_LABELS[log.action] || log.action,
      RESOURCE_LABELS[log.resource_type] || log.resource_type,
      log.resource_id || '',
      log.status
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Logs de Auditoría</h1>
          <p className="text-gray-600">
            Historial completo de acciones en el sistema
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-6 mb-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="start_date">Fecha Inicio</Label>
            <Input
              id="start_date"
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="end_date">Fecha Fin</Label>
            <Input
              id="end_date"
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="user_id">Usuario</Label>
            <Select
              value={filters.user_id || "all"}
              onValueChange={(value) => setFilters({ ...filters, user_id: value === "all" ? "" : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="action">Acción</Label>
            <Select
              value={filters.action || "all"}
              onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(ACTION_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Button type="submit" className="w-full md:w-auto">
              <Search className="mr-2 h-4 w-4" />
              Buscar
            </Button>
          </div>
        </form>
      </Card>

      {/* Logs Table */}
      <Card className="p-6">
        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-center py-8 text-gray-500">Cargando logs...</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Fecha/Hora</th>
                  <th className="text-left py-3 px-4">Usuario</th>
                  <th className="text-left py-3 px-4">Acción</th>
                  <th className="text-left py-3 px-4">Recurso</th>
                  <th className="text-left py-3 px-4">ID</th>
                  <th className="text-left py-3 px-4">Estado</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const user = users.find(u => u.id === log.user_id);
                  // Use combination of timestamp and user_id as unique key
                  const logKey = `${log.timestamp}-${log.user_id}-${log.action}`;
                  return (
                    <tr key={logKey} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-gray-400" />
                          <span>{user?.name || log.user_id.substring(0, 8)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-gray-400" />
                          {RESOURCE_LABELS[log.resource_type] || log.resource_type}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">
                        {log.resource_id ? log.resource_id.substring(0, 8) : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          log.status === 'success' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && logs.length === 0 && (
            <p className="text-center py-8 text-gray-500">
              No se encontraron logs con los filtros seleccionados
            </p>
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Mostrando {logs.length} registro(s)
        </div>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
